import type { AuthErrorCode } from './types.ts'

// The Cognito IDP failures we recognise, and the code each becomes. Values first, so the
// Record below stops compiling when a type is added without a translation.
export const COGNITO_ERROR_TYPES = [
  'NotAuthorizedException',
  'UserNotFoundException',
  'UserNotConfirmedException',
  'InvalidPasswordException',
  'InvalidParameterException',
  'CodeMismatchException',
  'EnableSoftwareTokenMFAException',
  'SoftwareTokenMFANotFoundException',
  'ExpiredCodeException',
  'LimitExceededException',
  'TooManyRequestsException',
  'TooManyFailedAttemptsException',
  'PasswordResetRequiredException',
  'UserLambdaValidationException',
  'ResourceNotFoundException',
  'ForbiddenException',
] as const

export type CognitoErrorType = (typeof COGNITO_ERROR_TYPES)[number]

/** What each failure means by default. An operation that disagrees passes an override. */
const DEFAULT_CODES: Record<CognitoErrorType, AuthErrorCode> = {
  NotAuthorizedException: 'invalid_credentials',
  UserNotFoundException: 'user_not_found',
  UserNotConfirmedException: 'user_not_confirmed',
  InvalidPasswordException: 'password_rejected',
  InvalidParameterException: 'invalid_parameter',
  CodeMismatchException: 'code_mismatch',
  // What VerifySoftwareToken answers to a code that does not match the secret it just issued.
  EnableSoftwareTokenMFAException: 'code_mismatch',
  // Asking to prefer a factor that was never verified: our own sequencing, not the user's.
  SoftwareTokenMFANotFoundException: 'idp_request_failed',
  ExpiredCodeException: 'code_expired',
  LimitExceededException: 'rate_limited',
  TooManyRequestsException: 'rate_limited',
  TooManyFailedAttemptsException: 'rate_limited',
  PasswordResetRequiredException: 'password_reset_required',
  // A trigger said no, the pool is gone, a WAF answered: none of them is the user's doing.
  UserLambdaValidationException: 'idp_request_failed',
  ResourceNotFoundException: 'idp_request_failed',
  ForbiddenException: 'idp_request_failed',
}

/** Per-operation reading of a shared failure. */
export type CognitoErrorOverrides = Partial<Record<CognitoErrorType, AuthErrorCode>>

function isKnownType(value: string): value is CognitoErrorType {
  return (COGNITO_ERROR_TYPES as readonly string[]).includes(value)
}

/** `__type` arrives bare or prefixed with `com.amazonaws.cognito.identity...#`. */
export function stripErrorPrefix(rawType: string): string {
  return rawType.split('#').pop() ?? rawType
}

/** The code for a `__type`, honouring the operation's own reading of it. */
export function authErrorCodeFor(
  rawType: string | undefined,
  overrides: CognitoErrorOverrides = {},
): AuthErrorCode {
  if (rawType === undefined) return 'idp_request_failed'
  const type = stripErrorPrefix(rawType)
  if (!isKnownType(type)) return 'idp_request_failed'
  return overrides[type] ?? DEFAULT_CODES[type]
}
